import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Typography,
  Box,
  CircularProgress,
  Breadcrumbs,
  Link,
  useTheme,
  alpha,
} from "@mui/material";
import {
  Close as CloseIcon,
  Folder as FolderIcon,
  Person as PersonIcon,
  Group as GroupIcon,
  ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";
import { PaletteColor } from "../types/PaletteColor.ts";
import { usePaletteColors } from "../hooks/useDarkMode.ts";

interface FolderPickerFolder {
  folderId: string;
  folderName: string;
}

interface FolderPickerBreadcrumb {
  id: string | null;
  name: string;
}

interface FolderPickerDialogProps {
  open: boolean;
  mode: "move" | "copy";
  palette: PaletteColor;
  hpcode: number;
  roomId: number; // 現在の部屋ID (0=マイフォルダのみ)
  honbuId: number;
  isMyFolderView: boolean; // ファイルがマイフォルダにあるか
  onClose: () => void;
  onConfirm: (targetRoomId: number, targetFolderId: string | null) => void;
}

type DestinationType = null | "myFolder" | "room";

export const FolderPickerDialog: React.FC<FolderPickerDialogProps> = ({
  open,
  mode,
  palette,
  hpcode,
  roomId,
  honbuId,
  isMyFolderView,
  onClose,
  onConfirm,
}) => {
  const { accentColor } = usePaletteColors(palette);
  const theme = useTheme();
  const [destType, setDestType] = useState<DestinationType>(null);
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [folders, setFolders] = useState<FolderPickerFolder[]>([]);
  const [breadcrumbs, setBreadcrumbs] = useState<FolderPickerBreadcrumb[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const actionLabel = mode === "move" ? "移動" : "コピー";

  // 移動モードではクロスルーム不可 → 同じルーム内のフォルダを直接表示
  // コピーモードではルーム種別選択を表示 (roomId>0の場合)
  const needsRoomSelection = mode === "copy" && roomId > 0;

  // ダイアログを開き直すたびにリセット
  useEffect(() => {
    if (open) {
      if (needsRoomSelection) {
        // コピーモード + 部屋あり → 種別選択画面から開始
        setDestType(null);
      } else {
        // 移動モード or roomId===0 → 現在のルームを自動選択
        setDestType(isMyFolderView ? "myFolder" : "room");
      }
      setCurrentFolderId(null);
      setFolders([]);
      setBreadcrumbs([]);
    }
  }, [open, roomId, mode, isMyFolderView, needsRoomSelection]);

  // フォルダ一覧の取得
  const fetchFolders = useCallback(
    async (targetRoomId: number, folderId: string | null) => {
      setIsLoading(true);
      try {
        const params = new URLSearchParams({
          hpcode: String(hpcode),
          roomId: String(targetRoomId),
          honbuId: String(honbuId),
        });
        if (folderId) params.set("folderId", folderId);

        const res = await fetch(`/api/files/list?${params}`);
        if (res.ok) {
          const data = await res.json();
          setFolders(
            (data.folders ?? []).map((f: any) => ({
              folderId: f.folderId,
              folderName: f.folderName,
            })),
          );
          setBreadcrumbs(data.breadcrumbs ?? []);
        }
      } catch (err) {
        console.error("フォルダ一覧の取得に失敗しました", err);
      } finally {
        setIsLoading(false);
      }
    },
    [hpcode, honbuId],
  );

  // destType選択 or フォルダ遷移時にフォルダ一覧を取得
  useEffect(() => {
    if (!open || destType === null) return;
    const targetRoomId = destType === "myFolder" ? 0 : roomId;
    fetchFolders(targetRoomId, currentFolderId);
  }, [open, destType, currentFolderId, fetchFolders, roomId]);

  const handleSelectDestType = (type: DestinationType) => {
    setDestType(type);
    setCurrentFolderId(null);
  };

  const handleEnterFolder = (folderId: string) => {
    setCurrentFolderId(folderId);
  };

  const handleBreadcrumbClick = (id: string | null) => {
    setCurrentFolderId(id);
  };

  const handleBack = () => {
    if (destType !== null && currentFolderId === null && needsRoomSelection) {
      // コピーモードで種別選択画面に戻る
      setDestType(null);
      setFolders([]);
      setBreadcrumbs([]);
    } else if (breadcrumbs.length >= 2) {
      // 一つ上のフォルダへ
      const parent = breadcrumbs[breadcrumbs.length - 2];
      setCurrentFolderId(parent.id);
    } else {
      setCurrentFolderId(null);
    }
  };

  const handleConfirm = () => {
    if (destType === null) return;
    const targetRoomId = destType === "myFolder" ? 0 : roomId;
    onConfirm(targetRoomId, currentFolderId);
  };

  // 現在のフォルダ名
  const currentFolderName =
    breadcrumbs.length > 0
      ? breadcrumbs[breadcrumbs.length - 1].name
      : destType === "myFolder"
        ? "マイフォルダ"
        : "部屋の共有フォルダ";

  const showBackButton =
    destType !== null && (currentFolderId !== null || needsRoomSelection);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      slotProps={{ paper: { sx: { borderRadius: 2, overflow: "hidden", minHeight: 350 } } }}
    >
      <Box
        sx={{
          background: `linear-gradient(to right, ${theme.palette.primary.main}, ${alpha(theme.palette.primary.main, 0.7)})`,
          color: "#fff",
          px: 2,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "3px solid #fff",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {showBackButton && (
            <IconButton size="small" onClick={handleBack} sx={{ color: "#fff", ml: -0.5 }}>
              <ArrowBackIcon />
            </IconButton>
          )}
          <Typography variant="h6" fontWeight="bold">
            別のフォルダに{actionLabel}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: "#fff" }}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Box sx={{ height: 3, bgcolor: theme.palette.primary.main }} />

      <DialogContent sx={{ px: 1 }}>
        {/* 種別選択画面 */}
        {destType === null && (
          <List>
            <ListItemButton onClick={() => handleSelectDestType("myFolder")}>
              <ListItemIcon>
                <PersonIcon />
              </ListItemIcon>
              <ListItemText primary="マイフォルダ" />
            </ListItemButton>
            {roomId > 0 && (
              <ListItemButton onClick={() => handleSelectDestType("room")}>
                <ListItemIcon>
                  <GroupIcon />
                </ListItemIcon>
                <ListItemText primary="部屋の共有フォルダ" />
              </ListItemButton>
            )}
          </List>
        )}

        {/* フォルダブラウズ画面 */}
        {destType !== null && (
          <>
            {/* パンくず */}
            {breadcrumbs.length > 0 && (
              <Breadcrumbs sx={{ px: 2, py: 1 }}>
                {breadcrumbs.map((bc, idx) => {
                  const isLast = idx === breadcrumbs.length - 1;
                  return isLast ? (
                    <Typography key={idx} variant="body2" fontWeight="bold">
                      {bc.name}
                    </Typography>
                  ) : (
                    <Link
                      key={idx}
                      component="button"
                      variant="body2"
                      underline="hover"
                      onClick={() => handleBreadcrumbClick(bc.id)}
                      sx={{ cursor: "pointer" }}
                    >
                      {bc.name}
                    </Link>
                  );
                })}
              </Breadcrumbs>
            )}

            {isLoading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  py: 4,
                }}
              >
                <CircularProgress size={32} />
              </Box>
            ) : folders.length === 0 ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ px: 2, py: 3, textAlign: "center" }}
              >
                サブフォルダはありません
              </Typography>
            ) : (
              <List dense>
                {folders.map((folder) => (
                  <ListItemButton
                    key={folder.folderId}
                    onClick={() => handleEnterFolder(folder.folderId)}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <FolderIcon sx={{ color: accentColor }} />
                    </ListItemIcon>
                    <ListItemText primary={folder.folderName} />
                  </ListItemButton>
                ))}
              </List>
            )}
          </>
        )}
      </DialogContent>

      {/* 確定ボタン: フォルダブラウズ画面で表示 */}
      {destType !== null && (
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} color="inherit">
            キャンセル
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirm}
            sx={{ bgcolor: accentColor, "&:hover": { bgcolor: accentColor, filter: "brightness(1.1)" } }}
          >
            ここに{actionLabel}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};
